import React, {
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { useAtom } from 'jotai'
import {
  Flex,
  FlexItem,
  GridCell,
  Select,
  Text,
  TEXT_VARIANT_ITALIC_LIGHT,
} from 'mailup-web-components'
import { type IntlShape, useIntl } from 'react-intl'

import { logAppError } from 'Helpers/logger'

import { getGeoRegions } from 'Pages/combinedFilterFormPage/services/specServices'
import {
  countriesAtom,
  countriesLoadingAtom,
} from 'Pages/combinedFilterFormPage/state/specState'
import { type UiGeoSpec } from '../../filterConditionTypes'
import { filterFormImmerAtom } from '../../state/state'
import { type ConditionProps, type GeoRegion } from '../../types'

const getPrecisionOptions = (
  intl: IntlShape,
): Array<{
  label: string
  value: UiGeoSpec['Precision']
}> => [
  {
    value: 1,
    label: intl.formatMessage({ id: 'Load.Filters.PrecisionLow' }),
  },
  {
    value: 2,
    label: intl.formatMessage({ id: 'Load.Filters.PrecisionMedium' }),
  },
  {
    value: 3,
    label: intl.formatMessage({ id: 'Load.Filters.PrecisionHigh' }),
  },
]

interface InputRegionProps {
  country: string
  value: UiGeoSpec['Region']
  onChange: (v: UiGeoSpec['Region']) => void
}

export const getGeoRegionsCallback = async ({
  setLoading,
  setError,
  setRegions,
  country,
}: {
  setLoading: React.Dispatch<React.SetStateAction<boolean>>
  setError: React.Dispatch<React.SetStateAction<boolean>>
  setRegions: React.Dispatch<React.SetStateAction<GeoRegion[]>>
  country: string
}): Promise<void> => {
  setError(false)
  if (country !== '') {
    setLoading(true)

    try {
      const regions = await getGeoRegions(country)
      setRegions(regions)
    } catch (e) {
      console.error('### error', e)
      logAppError({
        Component: 'CombinedFilterFormPage',
        Method: 'getGeoCountriesCallback',
        appError: e instanceof Error ? e : undefined,
      })
      setError(true)
    }
    setLoading(false)
  }
}

const InputRegion: FC<InputRegionProps> = ({ value, country, onChange }) => {
  const intl = useIntl()

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [regions, setRegions] = useState<GeoRegion[]>([])

  useEffect(() => {
    void getGeoRegionsCallback({
      setLoading,
      setError,
      setRegions,
      country,
    })
  }, [country])

  return (
    <Select
      options={regions}
      value={value}
      onChange={onChange}
      loading={loading}
      data-mu-test='input-geo-region'
      placeholder={intl.formatMessage({ id: 'Load.Filters.SelectRegion' })}
      disabled={country === ''}
      isError={error}
    />
  )
}

const GeoSpecCondition: FC<ConditionProps> = ({ blockIndex, condIndex }) => {
  const intl = useIntl()

  const [filter, setFilter] = useAtom(filterFormImmerAtom)
  const condition = filter.Blocks[blockIndex].Conditions[condIndex]
  const spec = condition.Spec as UiGeoSpec

  const [countries] = useAtom(countriesAtom)
  const [countriesLoading] = useAtom(countriesLoadingAtom)

  const precisionOptions = useMemo(() => getPrecisionOptions(intl), [intl])

  const handleChangeCountry = useCallback(
    (v: UiGeoSpec['Country']): void => {
      setFilter((draft) => {
        const draftSpec = draft.Blocks[blockIndex].Conditions[condIndex]
          .Spec as UiGeoSpec
        draftSpec.Country = v ?? ''
        draftSpec.Region = ''
      })
    },
    [blockIndex, condIndex, setFilter],
  )

  const handleChangeRegion = useCallback(
    (v: UiGeoSpec['Region']): void => {
      setFilter((draft) => {
        const draftSpec = draft.Blocks[blockIndex].Conditions[condIndex]
          .Spec as UiGeoSpec
        draftSpec.Region = v ?? ''
      })
    },
    [blockIndex, condIndex, setFilter],
  )

  const handleChangePrecision = useCallback(
    (v: UiGeoSpec['Precision']): void => {
      setFilter((draft) => {
        const draftSpec = draft.Blocks[blockIndex].Conditions[condIndex]
          .Spec as UiGeoSpec
        draftSpec.Precision = v
      })
    },
    [blockIndex, condIndex, setFilter],
  )

  const description = useMemo(() => {
    switch (spec.GeoMatchOperator) {
      case 'LocatedIn':
        return spec.Precision === 3
          ? intl.formatMessage({ id: 'Load.Filters.GeoDescriptionCase1' })
          : spec.Precision === 2
            ? intl.formatMessage({ id: 'Load.Filters.GeoDescriptionCase2' })
            : intl.formatMessage({ id: 'Load.Filters.GeoDescriptionCase1' })
      case 'NotLocatedIn':
        return spec.Precision === 3
          ? intl.formatMessage({ id: 'Load.Filters.GeoDescriptionCase4' })
          : spec.Precision === 2
            ? intl.formatMessage({ id: 'Load.Filters.GeoDescriptionCase5' })
            : intl.formatMessage({ id: 'Load.Filters.GeoDescriptionCase6' })
    }
  }, [spec.Precision, spec.GeoMatchOperator, intl])

  return (
    <>
      <GridCell span={3}>
        <Select
          options={countries}
          value={spec.Country}
          onChange={handleChangeCountry}
          loading={countriesLoading}
          data-mu-test='input-geo-country'
          placeholder={intl.formatMessage({ id: 'Load.Filters.SelectCountry' })}
        />
      </GridCell>
      <GridCell span={3}>
        <InputRegion
          value={spec.Region}
          country={spec.Country}
          onChange={handleChangeRegion}
        />
      </GridCell>
      <GridCell span={9}>
        <Flex alignItems='center' gap='8px'>
          <Text>
            {intl.formatMessage({
              id: 'Load.Filters.GeoGlobalPrecisionSelect',
            })}
          </Text>
          <FlexItem flexGrow={1}>
            <Select
              data-mu-test='input-geo-precision'
              value={spec.Precision}
              options={precisionOptions}
              onChange={handleChangePrecision}
              searchable={false}
            />
          </FlexItem>
          <Text>
            {intl.formatMessage({
              id: 'Load.Filters.GeoGlobalPrecisionSelect2',
            })}
          </Text>
        </Flex>
      </GridCell>
      <GridCell span={12}>
        <Text variant={TEXT_VARIANT_ITALIC_LIGHT}>{description}</Text>
      </GridCell>
    </>
  )
}

export default GeoSpecCondition
